import {
  Assets,
  Container,
  FederatedPointerEvent,
  Graphics,
  MaskFilter,
  Rectangle,
  Sprite,
  Text,
} from 'pixi.js'

// new mask input

export class Side {
  public readonly container: Container
  public readonly width: number
  public readonly height: number
  private origin: { x: number; y: number }
  private baseImageScale: number
  private baseImageHeight: number
  private dynScale: number
  private sections: Map<Section, Container>
  private layerContainers: Map<Layer, Container>
  private sectionOutlines: Map<Section, Graphics>
  private sectionMasks: Map<Section, MaskFilter>
  private maskCleanup: Map<Section, () => void>
  public bg_image: Promise<Sprite> | null
  public overlay?: Container
  private dm: DragManager
  private draggables: Map<Layer, Container>
  private layerCleanup: Map<ImageLayer | TextLayer, () => void>
  private showingOutlines: boolean
  private printSide: 'front' | 'back'

  public constructor(
    width: number,
    height: number,
    origin: { x: number; y: number },
    dm: DragManager
  ) {
    this.container = new Container()
    this.container.sortableChildren = true
    this.container.setSize(width, height)
    this.container.width = width
    this.container.height = height
    this.width = width
    this.height = height
    this.origin = origin
    this.sections = new Map()
    this.bg_image = null
    this.layerContainers = new Map()
    this.baseImageScale = 1
    this.baseImageHeight = 1
    this.dynScale = 1
    this.overlay = new Container()
    this.draggables = new Map()
    this.layerCleanup = new Map()
    this.sectionOutlines = new Map()
    this.sectionMasks = new Map()
    this.maskCleanup = new Map()
    this.dm = dm
    this.showingOutlines = false
    this.printSide = 'front'
  }

  public setBaseImage(url: string) {
    if (this.bg_image == null) {
      this.bg_image = Assets.load(url).then(() => {
        const background = Sprite.from(url)
        this.baseImageScale = this.height / background.height
        background.zIndex = 0
        background.scale.set(this.baseImageScale)
        this.baseImageHeight = background.height
        this.container.addChildAt(background, 0)
        return background
      })
    } else {
      this.bg_image = this.bg_image
        .then((bg) => bg.destroy())
        .then(() => Assets.load(url))
        .then(() => {
          const background = Sprite.from(url)
          this.baseImageScale = this.height / background.height
          background.zIndex = 0
          background.scale.set(this.baseImageScale)
          this.baseImageHeight = background.height
          this.container.addChildAt(background, 0)

          background.scale.set(this.baseImageScale * this.dynScale)
          const offset =
            (background.height - this.baseImageHeight) *
            (-this.origin.y / this.baseImageHeight)
          background.position.set(this.width / 2 - background.width / 2, offset)
          return background
        })
    }
    return this
  }

  public setBaseImageScale(scale: number) {
    this.dynScale = scale
    this.bg_image = this.bg_image.then((bg) => {
      bg.scale.set(this.baseImageScale * scale)
      const offset =
        (bg.height - this.baseImageHeight) *
        (-this.origin.y / this.baseImageHeight)
      bg.position.set(this.width / 2 - bg.width / 2, offset)
      return bg
    })
    return this
  }

  public addSection(
    section: Section,
    transform: { x: number; y: number; rot?: number },
    clip: { x: number; y: number; w: number; h: number } = {
      x: 0,
      y: 0,
      w: 1,
      h: 1,
    }
  ) {
    const sectionWrapper = new Container()
    sectionWrapper.zIndex = 50
    sectionWrapper.position.set(
      transform.x + this.origin.x,
      transform.y + this.origin.y
    )
    const clipPath = new Graphics()
      .rect(clip.x, clip.y, section.width * clip.w, section.height * clip.h)
      .fill(0xffffff)

    sectionWrapper.addChild(clipPath)
    sectionWrapper.mask = clipPath
    this.container.addChild(sectionWrapper)

    const sectionContainer = new Container()
    sectionContainer.position.set(0, 0)
    sectionWrapper.addChild(sectionContainer)
    this.sections.set(section, sectionContainer)

    const width = 10 / this.container.scale.x
    const sectionOutline = new Graphics()
      .rect(
        sectionWrapper.position.x - width,
        sectionWrapper.position.y - width,
        section.width * clip.w + width * 2,
        section.height * clip.h + width * 2
      )
      .stroke({ width, color: 'pink' })
    sectionOutline.visible = this.showingOutlines

    this.container.addChild(sectionOutline)
    this.sectionOutlines.set(section, sectionOutline)

    section.on('added', this.onAdded(sectionContainer, section).bind(this))
    section.on('removed', this.onRemoved(sectionContainer).bind(this))
    section.on('masked', (layer) => {
      if (layer != null) {
        const obj = Sprite.from(layer.getImage().src)
        const tr = layer.getTransform()
        obj.position.set(tr.x, tr.y)
        obj.scale.set(tr.scaleX, tr.scaleY)
        sectionContainer.addChild(obj)
        const filter = new MaskFilter({ sprite: obj })
        obj.renderable = false
        filter.blendMode = sectionContainer.blendMode
        for (const layer of Array.from(section.maskable.values())) {
          const container = this.layerContainers.get(layer)
          if (container != null) {
            container.blendMode = 'normal'
            container.filters = [filter]
          }
        }
        const draggable = new Transformable(obj, this.dm, (c) => {
          layer.alterTransform(
            {
              x: c.position.x,
              y: c.position.y,
              scaleX: c.scale.x,
              scaleY: c.scale.y,
            },
            this
          )
        })
        const removeChangeListener = layer.onChange(async (tr, src) => {
          if (src !== this) {
            obj.position.set(tr.x, tr.y)
            obj.scale.set(tr.scaleX, tr.scaleY)
            obj.texture = await Assets.load({
              src: layer.getImage().src,
              loadParser: 'loadTextures',
            })
          }
        })
        this.overlay.addChild(draggable)
        this.sectionMasks.set(section, filter)
        this.maskCleanup.set(section, () => {
          removeChangeListener()
          this.overlay.removeChild(draggable)
          this.draggables.delete(layer)
          draggable.destroy()
        })
      } else {
        for (let i = 0; i < sectionContainer.children.length; i++) {
          const container = sectionContainer.children[i]
          if (
            Array.isArray(container.filters) &&
            container.filters.length > 0
          ) {
            const filter = container.filters[0] as MaskFilter
            sectionContainer.removeChild(filter.sprite)
            container.filters = []
            container.blendMode = filter.blendMode
          }
        }
        this.sectionMasks.delete(section)
        this.maskCleanup.get(section)?.()
        this.maskCleanup.delete(section)
      }
    })

    return this
  }

  public setCompositeOperation(composite: string) {
    this.sections.forEach((sectionContainer) => {
      for (let i = 0; i < sectionContainer.children.length; i++) {
        sectionContainer.blendMode = composite as any
        const container = sectionContainer.children[i]
        if (container != null) {
          if (
            Array.isArray(container.filters) &&
            container.filters.length > 0
          ) {
            container.filters[0].blendMode = composite as any
          } else {
            container.blendMode = composite as any
          }
        }
      }
    })
    return this
  }

  public reset() {
    this.sections.forEach((_, section) => section.reset())
  }

  public showOutlines(show = true) {
    this.showingOutlines = show
    this.sectionOutlines.forEach((outline, section) => {
      if (section.hasLayers()) {
        outline.visible = show
      }
    })
  }

  private onAdded(sectionContainer: Container, section: Section) {
    return (layer: TextLayer | ImageLayer, maskable: boolean, z?: number) => {
      if (layer instanceof ImageLayer) {
        const image = layer.getImage()
        const obj = Sprite.from(image.src)
        obj.scale.set(layer.getTransform().scaleX, layer.getTransform().scaleY)
        obj.position.set(layer.getTransform().x, layer.getTransform().y)
        z != null
          ? sectionContainer.addChildAt(obj, z)
          : sectionContainer.addChild(obj)
        const drag = new Transformable(obj, this.dm, (c) => {
          layer.alterTransform(
            {
              x: c.position.x,
              y: c.position.y,
              scaleX: c.scale.x,
              scaleY: c.scale.y,
            },
            this
          )
        })

        const remove = layer.onChange(async (tr, src) => {
          if (src !== this) {
            obj.position.set(tr.x, tr.y)
            obj.scale.set(tr.scaleX, tr.scaleY)
            obj.texture = await Assets.load({
              src: image.src,
              loadParser: 'loadTextures',
            })
            obj.visible = layer.shouldDisplay(this.printSide)
            drag.visible = layer.shouldDisplay(this.printSide)
          }
        })

        this.overlay.addChild(drag)
        this.draggables.set(layer, drag)
        this.layerCleanup.set(layer, remove)
        this.layerContainers.set(layer, obj)

        if (maskable) {
          const filter = this.sectionMasks.get(section)
          if (filter) {
            obj.filters = [filter]
            obj.blendMode = 'normal'
          }
        }
      } else {
        const obj = new Text({
          text: layer.getText(),
          style: {
            fill: layer.fill,
            fontFamily: layer.fontFamily,
            fontSize: layer.fontSize,
          },
        })
        sectionContainer.addChild(obj)
        const drag = new Draggable(obj, this.dm, (c) => {
          layer.alterTransform(
            {
              x: c.position.x,
              y: c.position.y,
            },
            this
          )
        })

        const remove = layer.onChange((tr, src) => {
          if (src !== this) {
            obj.position.set(tr.x, tr.y)
            obj.scale.set(tr.scaleX, tr.scaleY)
            obj.style.fill = layer.fill
            obj.style.fontFamily = layer.fontFamily
            obj.style.fontSize = layer.fontSize
            obj.visible = layer.shouldDisplay(this.printSide)
            drag.visible = layer.shouldDisplay(this.printSide)
          }
        })

        this.overlay.addChild(drag)
        this.draggables.set(layer, drag)
        this.layerCleanup.set(layer, remove)
        this.layerContainers.set(layer, obj)
      }
    }
  }

  public async ensureAssets() {
    for (const [layer, obj] of Array.from(this.layerContainers.entries())) {
      if (layer instanceof ImageLayer) {
        const texture = await Assets.load({
          src: layer.getImage().src,
          loadParser: 'loadTextures',
        })
        ;(obj as Sprite).texture = texture
      }
    }
  }

  public setPrintSideDisplay(side: 'front' | 'back') {
    this.printSide = side
    this.layerContainers.forEach((cont, layer) => {
      const visible = layer.shouldDisplay(this.printSide)
      cont.visible = visible
      const draggable = this.draggables.get(layer)
      if (draggable != null) {
        draggable.visible = visible
      }
    })
  }

  private onRemoved(sectionContainer: Container) {
    return (layer: TextLayer | ImageLayer) => {
      this.layerCleanup.get(layer)?.()
      this.layerCleanup.delete(layer)
      const layerContainer = this.layerContainers.get(layer)
      if (layerContainer != null) {
        sectionContainer.removeChild(this.layerContainers.get(layer))
        layerContainer.destroy()
      }
      const draggable = this.draggables.get(layer)
      if (draggable != null) {
        this.overlay.removeChild(draggable)
        this.draggables.delete(layer)
        draggable.destroy()
      }
    }
  }
}

export class Transform {
  public y: number
  public x: number
  public scaleX: number
  public scaleY: number
  public rotation: number

  public constructor(
    top: number,
    left: number,
    scaleX: number,
    scaleY: number,
    rotation = 0
  ) {
    this.y = top
    this.x = left
    this.scaleX = scaleX
    this.scaleY = scaleY
    this.rotation = rotation
  }

  public clone(): Transform {
    return new Transform(
      this.y,
      this.x,
      this.scaleX,
      this.scaleY,
      this.rotation
    )
  }
}

export class Section {
  public readonly width: number
  public readonly height: number
  public readonly clip: { x: number; y: number; w: number; h: number }
  private name: string | number
  private layers: Set<ImageLayer | TextLayer>
  private mask: ImageLayer | null
  public maskable: Set<ImageLayer | TextLayer>
  private listeners: {
    added: Set<
      (layer: ImageLayer | TextLayer, maskable: boolean, z?: number) => void
    >
    removed: Set<(layer: ImageLayer | TextLayer) => void>
    masked: Set<(src: ImageLayer | null) => void>
    cleared: Set<() => void>
  }

  public constructor(
    name: string | number,
    { width, height }: { width: number; height: number }
  ) {
    this.name = name
    this.width = width
    this.height = height
    this.layers = new Set()
    this.listeners = {
      added: new Set(),
      removed: new Set(),
      masked: new Set(),
      cleared: new Set(),
    }
    this.mask = null
    this.maskable = new Set()
  }

  public setMask(src: HTMLImageElement | ImageLayer | null) {
    if (src == null) {
      this.mask = null
    } else {
      this.mask = src instanceof ImageLayer ? src : new ImageLayer(src)
    }
    this.listeners.masked.forEach((cb) => cb(this.mask))
    return this.mask
  }

  public getName() {
    return this.name
  }

  public hasLayers() {
    return this.layers.size > 0
  }

  public addText(text: string): TextLayer {
    const layer = new TextLayer(text, new Transform(0, 0, 1, 1))
    return this.addLayer(layer)
  }

  public addImage(
    image: HTMLImageElement,
    maskable = false,
    z?: number
  ): ImageLayer {
    const layer = new ImageLayer(image, new Transform(0, 0, 1, 1))
    return this.addLayer(layer, maskable, z)
  }

  public addLayer<T extends ImageLayer | TextLayer>(
    layer: T,
    maskable = false,
    z?: number
  ): T {
    layer.setContext(this)
    this.layers.add(layer)
    if (maskable) {
      this.maskable.add(layer)
    }
    this.listeners.added.forEach((cb) => cb(layer, maskable, z))
    return layer
  }

  public removeLayer(layer: ImageLayer | TextLayer): boolean {
    if (this.layers.delete(layer)) {
      this.maskable.delete(layer)
      this.listeners.removed.forEach((cb) => cb(layer))
      return true
    } else {
      return false
    }
  }

  public reset() {
    this.layers.clear()
  }

  public on(event: 'masked', cb: (src: ImageLayer | null) => void): () => void
  public on(
    event: 'added' | 'removed',
    cb: (layer: ImageLayer | TextLayer) => void
  ): () => void
  public on(event: 'cleared', cb: () => void): () => void
  public on(event: string, cb: any) {
    this.listeners[event].add(cb)

    return () => {
      this.listeners[event].delete(cb)
    }
  }
}

interface Bounds {
  width: number
  height: number
}

export abstract class Layer {
  protected transform: Transform
  protected ctx: Bounds
  protected display: 'front' | 'back' | 'both'

  private changeListeners: Set<(tr: Transform, source?: any) => void>

  public constructor(initial: Transform) {
    this.transform = initial
    this.ctx = { width: 0, height: 0 }
    this.changeListeners = new Set()
    this.display = 'both'
  }

  public applyTransform(transform: Transform, source: any = null) {
    this.transform = transform
    this.runChangeListeners(source)
  }

  public alterTransform(data: Partial<Transform>, source: any = null) {
    this.transform.y = data.y ?? this.transform.y
    this.transform.x = data.x ?? this.transform.x
    this.transform.scaleX = data.scaleX ?? this.transform.scaleX
    this.transform.scaleY = data.scaleY ?? this.transform.scaleY
    this.runChangeListeners(source)
  }

  public reset() {
    this.transform.y = 0
    this.transform.x = 0
    this.transform.scaleX = 1
    this.transform.scaleY = 1
    this.runChangeListeners(null)
  }

  public center(axis: 'x' | 'y' | 'both' = 'both') {
    const bounds = this.getRect()
    if (axis === 'x' || axis === 'both') {
      this.transform.x = this.ctx.width / 2 - bounds.width / 2
    }
    if (axis === 'y' || axis === 'both') {
      this.transform.y = this.ctx.height / 2 - bounds.height / 2
    }
    this.runChangeListeners(null)
  }

  public setContext(context: Bounds) {
    this.ctx = context
  }

  public onChange(cb: (tr: Transform, source: any) => void): () => void {
    this.changeListeners.add(cb)
    return () => {
      this.changeListeners.delete(cb)
    }
  }

  protected runChangeListeners(source: any) {
    this.changeListeners.forEach((cb) => cb(this.transform, source))
  }

  public getTransform(): Transform {
    return this.transform
  }

  public abstract getRect(): Bounds

  public isContainedInContext(): boolean {
    const width = 0
    const height = 0
    const top = this.transform.y
    const left = this.transform.x
    const right = left + width
    const bottom = top + height

    return (
      top >= 0 &&
      left >= 0 &&
      right <= this.ctx.width &&
      bottom <= this.ctx.height
    )
  }

  public getDisplay() {
    return this.display
  }

  public setDisplay(when: 'front' | 'back' | 'both', source: any = null) {
    this.display = when
    this.runChangeListeners(source)
  }

  public shouldDisplay(when: 'front' | 'back') {
    return this.display === 'both' || this.display === when
  }
}

export class ImageLayer extends Layer {
  public constructor(
    private image: HTMLImageElement,
    initial: Transform = new Transform(0, 0, 1, 1)
  ) {
    super(initial)
  }

  public maximize() {
    const { width, height } = this.ctx
    const { width: w, height: h } = this.image
    const scale = Math.min(width / w, height / h)
    this.transform.scaleX = scale
    this.transform.scaleY = scale
  }

  public getImage(): HTMLImageElement {
    return this.image
  }

  public setSource(src: string): Promise<void> {
    this.image.src = src
    return new Promise((res) => {
      const resolver = () => {
        this.image.removeEventListener('load', resolver)
        res(undefined)
      }
      this.image.addEventListener('load', resolver)
    }).then(() => {
      this.runChangeListeners(null)
    })
  }

  public clone(): ImageLayer {
    return new ImageLayer(this.image, this.transform.clone())
  }

  public getRect(): Bounds {
    return {
      width: this.image.width * this.transform.scaleX,
      height: this.image.height * this.transform.scaleY,
    }
  }
}

export class TextLayer extends Layer {
  public fill = '#000000'
  public fontFamily = 'Arial'
  public fontSize = 16
  public styles = {}

  public constructor(private text: string, initial: Transform) {
    super(initial)
  }

  public getText(): string {
    return this.text
  }

  public setText(text: string) {
    this.text = text
    this.runChangeListeners(null)
  }

  public setColor(color: string) {
    this.fill = color
    this.runChangeListeners(null)
  }

  public setFont(font: string) {
    this.fontFamily = font
    this.runChangeListeners(null)
  }

  public setFontSize(size: number) {
    this.fontSize = size
    this.runChangeListeners(null)
  }

  public clone(): TextLayer {
    return new TextLayer(this.text, this.transform.clone())
  }

  public getRect(): Bounds {
    return {
      width: 0,
      height: 0,
    }
  }
}

export class Draggable extends Container {
  private object: Container
  private box: Graphics
  private cb: (c: Container) => void

  public constructor(
    object: Container,
    dragManager: DragManager,
    cb: (c: Container) => void
  ) {
    super()
    this.eventMode = 'static'
    this.cursor = 'move'
    this.object = object

    this.box = new Graphics()
    this.addChild(this.box)
    this.box.eventMode = 'static'

    this.tick()

    dragManager.onDrag(this.box, this.moveInit.bind(this), this.move.bind(this))
    this.cb = cb
  }

  public tick() {
    const size = this.object.getSize()
    const topLeft = this.object.getGlobalPosition()
    const bottomRight = this.object.parent.toGlobal({
      x: this.object.position.x + size.width,
      y: this.object.position.y + size.height,
    })
    const width = bottomRight.x - topLeft.x
    const height = bottomRight.y - topLeft.y
    this.box
      .clear()
      .rect(-2.5, -2.5, width + 5, height + 5)
      .stroke({ width: 1, color: 'blue' })

    this.box.hitArea = new Rectangle(0, 0, this.box.width, this.box.height)
    this.parent?.toLocal(topLeft, undefined, this.position)
  }

  private moveInit(event: FederatedPointerEvent) {
    const global = this.object.getGlobalPosition()
    return {
      x: event.global.x - global.x,
      y: event.global.y - global.y,
    }
  }

  private move(
    event: FederatedPointerEvent,
    target: Graphics,
    offset: ReturnType<typeof this.moveInit>
  ) {
    const moved = {
      x: event.global.x - offset.x,
      y: event.global.y - offset.y,
    }
    target.parent.parent.toLocal(moved, undefined, target.parent.position)
    target.position.x = Math.floor(target.position.x)
    target.position.y = Math.floor(target.position.y)
    this.object.parent.toLocal(moved, undefined, this.object.position)
    this.object.position.x = Math.floor(this.object.position.x)
    this.object.position.y = Math.floor(this.object.position.y)
    this.cb?.(this.object)
  }
}

export class Transformable extends Container {
  private object: Container
  private box: Graphics
  private dragPoints: {
    tl: Graphics
    tr: Graphics
    bl: Graphics
    br: Graphics
  }
  private onChange: (c: Container) => void

  public constructor(
    object: Container,
    dragManager: DragManager,
    onChange: (c: Container) => void
  ) {
    super()
    this.eventMode = 'static'
    this.cursor = 'move'
    this.object = object

    this.box = new Graphics()
    this.addChild(this.box)
    this.box.eventMode = 'static'

    this.dragPoints = {
      tl: new Graphics({ cursor: 'nwse-resize' }),
      tr: new Graphics({ cursor: 'nesw-resize' }),
      bl: new Graphics({ cursor: 'nesw-resize' }),
      br: new Graphics({ cursor: 'nwse-resize' }),
    }

    this.addChild(this.dragPoints.tl)
    this.addChild(this.dragPoints.tr)
    this.addChild(this.dragPoints.bl)
    this.addChild(this.dragPoints.br)

    this.dragPoints.tl.eventMode = 'static'
    this.dragPoints.tr.eventMode = 'static'
    this.dragPoints.bl.eventMode = 'static'
    this.dragPoints.br.eventMode = 'static'

    this.tick()

    dragManager.onDrag(this.box, this.moveInit.bind(this), this.move.bind(this))
    dragManager.onDrag(
      this.dragPoints.tl,
      this.scaleInitTL.bind(this),
      this.doScale.bind(this)
    )
    dragManager.onDrag(
      this.dragPoints.tr,
      this.scaleInitTR.bind(this),
      this.doScale.bind(this)
    )
    dragManager.onDrag(
      this.dragPoints.bl,
      this.scaleInitBL.bind(this),
      this.doScale.bind(this)
    )
    dragManager.onDrag(
      this.dragPoints.br,
      this.scaleInitBR.bind(this),
      this.doScale.bind(this)
    )

    this.onChange = onChange
  }

  public tick() {
    const size = this.object.getSize()
    const topLeft = this.object.getGlobalPosition()
    const bottomRight = this.object.parent.toGlobal({
      x: this.object.position.x + size.width,
      y: this.object.position.y + size.height,
    })
    const width = bottomRight.x - topLeft.x
    const height = bottomRight.y - topLeft.y
    this.box
      .clear()
      .rect(0, 0, width, height)
      .stroke({ width: 1, color: 'blue' })

    this.box.hitArea = new Rectangle(0, 0, this.box.width, this.box.height)

    this.dragPoints.tr
      .clear()
      .rect(width - 5, -5, 10, 10)
      .stroke({ width: 1, color: 'blue' })
      .fill({ alpha: 0 })
    this.dragPoints.tl
      .clear()
      .rect(-5, -5, 10, 10)
      .stroke({ width: 1, color: 'blue' })
      .fill({ alpha: 0 })
    this.dragPoints.bl
      .clear()
      .rect(-5, height - 5, 10, 10)
      .stroke({ width: 1, color: 'blue' })
      .fill({ alpha: 0 })
    this.dragPoints.br
      .clear()
      .rect(width - 5, height - 5, 10, 10)
      .stroke({ width: 1, color: 'blue' })
      .fill({ alpha: 0 })

    this.parent?.toLocal(topLeft, undefined, this.position)
  }

  private moveInit(event: FederatedPointerEvent) {
    const global = this.object.getGlobalPosition()
    return {
      x: event.global.x - global.x,
      y: event.global.y - global.y,
    }
  }

  private move(
    event: FederatedPointerEvent,
    target: Graphics,
    offset: ReturnType<typeof this.moveInit>
  ) {
    const moved = {
      x: event.global.x - offset.x,
      y: event.global.y - offset.y,
    }
    target.parent.parent.toLocal(moved, undefined, target.parent.position)
    target.position.x = Math.floor(target.position.x)
    target.position.y = Math.floor(target.position.y)
    this.object.parent.toLocal(moved, undefined, this.object.position)
    this.object.position.x = Math.floor(this.object.position.x)
    this.object.position.y = Math.floor(this.object.position.y)
    this.onChange?.(this.object)
  }

  private scaleInitTL() {
    return {
      originX: this.object.x + this.object.width,
      originY: this.object.y + this.object.height,
      scaleX: this.object.scale.x,
      scaleY: this.object.scale.y,
      diagonalLengthSq:
        this.object.width * this.object.width +
        this.object.height * this.object.height,
    }
  }

  private scaleInitTR() {
    return {
      originX: this.object.x,
      originY: this.object.y + this.object.height,
      scaleX: this.object.scale.x,
      scaleY: this.object.scale.y,
      diagonalLengthSq:
        this.object.width * this.object.width +
        this.object.height * this.object.height,
    }
  }

  private scaleInitBL() {
    return {
      originX: this.object.x + this.object.width,
      originY: this.object.y,
      scaleX: this.object.scale.x,
      scaleY: this.object.scale.y,
      diagonalLengthSq:
        this.object.width * this.object.width +
        this.object.height * this.object.height,
    }
  }

  private scaleInitBR() {
    return {
      originX: this.object.x,
      originY: this.object.y,
      scaleX: this.object.scale.x,
      scaleY: this.object.scale.y,
      diagonalLengthSq:
        this.object.width * this.object.width +
        this.object.height * this.object.height,
    }
  }

  private doScale(
    event: FederatedPointerEvent,
    _control: any,
    initial: ReturnType<typeof this.scaleInitTL>
  ) {
    const newPosition = event.global
    const local = this.object.parent.toLocal(newPosition)

    const deltaX = Math.abs(local.x - initial.originX)
    const deltaY = Math.abs(local.y - initial.originY)

    const newDiagonalLength = deltaX * deltaX + deltaY * deltaY

    const scaleFactor = Math.sqrt(newDiagonalLength / initial.diagonalLengthSq)

    const scaleX = Math.max(0, initial.scaleX * scaleFactor)
    const scaleY = Math.max(0, initial.scaleY * scaleFactor)

    const pH = this.object.height
    const pW = this.object.width

    this.object.scale.set(scaleX, scaleY)

    const { width, height } = this.object.getSize()
    const nX =
      this.object.x + (initial.originX - this.object.x) * (1 - width / pW)
    const nY =
      this.object.y + (initial.originY - this.object.y) * (1 - height / pH)

    this.object.position.set(nX, nY)
    this.onChange?.(this.object)
  }
}

export class DragManager {
  private dragCleanup: () => void | null
  private root: Container
  private cleanup: Map<Container, () => any>

  public constructor(root: Container) {
    this.dragCleanup = null
    this.root = root
    this.cleanup = new Map()

    this.root.on('pointerup', () => {
      this.dragCleanup?.()
    })
  }

  public setRoot(root: Container) {
    this.root = root
    this.root.on('pointerup', () => {
      this.dragCleanup?.()
    })
  }

  public onDrag<T, C extends Container>(
    object: C,
    init: (event: FederatedPointerEvent, object: C) => T,
    onDrag: (event: FederatedPointerEvent, object: C, data: T) => void,
    shift?: boolean
  ) {
    this.cleanup.get(object)?.()
    const cb = (event: FederatedPointerEvent) => {
      if (shift && !event.shiftKey) return
      if (!shift && event.shiftKey) return
      if (event.altKey) return
      event.stopPropagation()
      event.preventDefault()
      if (this.dragCleanup == null) {
        const data = init?.(event, object)

        const cb = (e: FederatedPointerEvent) => {
          onDrag(e, object, data)
        }

        this.root.on('pointermove', cb)
        this.dragCleanup = () => {
          this.root.off('pointermove', cb)
          this.dragCleanup = null
        }
      }
    }

    object.on('pointerdown', cb)
    this.cleanup.set(object, () => {
      object.off('pointerdown', cb)
    })
  }

  public off(sprite: Container) {
    this.cleanup.get(sprite)?.()
    this.cleanup.delete(sprite)
  }
}
